import React, { useCallback, useRef, useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ReactFlow,
  Background,
  Controls,
  Panel,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Button,
  Card,
  Badge,
  Modal,
  Input,
  NotificationModal,
  Header,
  CollapsibleSection,
  ConfirmDialog,
  LoadingSpinner,
} from "@tadagram/ui";
import "@tadagram/ui/styles.css";
import { Play, Save, Upload, Download, Trash2, Plus, Undo2, Redo2, ChevronsRight, ChevronsLeft, Copy, ClipboardPaste, CopyPlus, Maximize, Minimize, Eye, PanelRight, SquareStack, FilePlus, Power, PowerOff, RotateCw } from "lucide-react";
import { useTour } from '../components/tour';
import { useWorkflowStore } from "./WorkflowBuilder/store";
import { NODE_TYPES } from "./WorkflowBuilder/types";
import { NODE_ICONS, CATEGORY_STYLES, NODE_CATEGORY_MAP } from "./WorkflowBuilder/CustomNode";
import { useNodeRegistry } from "../hooks/useNodeRegistry";
import { WorkflowConverter } from "./WorkflowBuilder/converter";
import { WorkflowAPI, TaskAPI } from "./WorkflowBuilder/api";
import { apiService } from "../services/api";
import { NodeConfigPanel } from "./WorkflowBuilder/NodeConfigPanel";
import { customNodeTypes } from "./WorkflowBuilder/CustomNode";
import { DATA_NODE_TYPES, ZONE_SLOTS, DATA_ZONE_ID } from "./WorkflowBuilder/DataZoneNode";
import { validateSingleNode, validateAllNodes } from "./WorkflowBuilder/validateNode";
import { ContextMenu } from "./WorkflowBuilder/ContextMenu";
import { WorkflowListPanel } from "./WorkflowBuilder/WorkflowListPanel";
import { WorkflowExportModal } from "./WorkflowBuilder/WorkflowExportModal";
import { WORKFLOW_TEMPLATES, applyTemplate, applyMultiWorkflowTemplate, WorkflowTemplate } from "./WorkflowBuilder/workflowTemplates";
import { TemplateSetupGuideModal } from "./WorkflowBuilder/TemplateSetupGuideModal";

// Module-level flag: survives component remounts (React Router optional-param
// route changes can remount the component, resetting useRef values).
let _skipAutoRedirect = false;

const WorkflowBuilderInner: React.FC = () => {
  const { t } = useTranslation('workflow');
  const { startTour } = useTour();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const builderRootRef = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition } = useReactFlow();

  // Fullscreen mode
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showWorkflowList, setShowWorkflowList] = useState(true);

  // 🆕 Load node types from backend registry
  const { 
    nodeTypes: registryNodeTypes, 
    loading: registryLoading, 
    error: registryError 
  } = useNodeRegistry();

  const mergedNodeTypes = React.useMemo(() => {
    if (registryNodeTypes.length === 0) return NODE_TYPES;
    const byType = new Map<string, any>();
    for (const node of NODE_TYPES) byType.set(node.type, node);
    for (const node of registryNodeTypes) byType.set(node.type, node);
    return Array.from(byType.values());
  }, [registryNodeTypes]);

  // Use registry nodes if available, fallback to hardcoded
  const HIDDEN_NODE_TYPES = ['search_groups', 'ai_grok_remake_image'];
  const NODE_TYPES_TO_USE = mergedNodeTypes
    .filter((nt: any) => !HIDDEN_NODE_TYPES.includes(nt.type));

  // Badges shown above specific nodes in the sidebar palette
  const NODE_BADGES: Record<string, { labelKey: string; color: 'new' | 'dev' }> = {
    find_group_posts: { labelKey: 'badge_new', color: 'new' },
    find_top_posts: { labelKey: 'badge_new', color: 'new' },
    ai_generate_asset: { labelKey: 'badge_inDevelopment', color: 'dev' },
    ai_remake_image: { labelKey: 'badge_inDevelopment', color: 'dev' },
    ai_grok_remake_image: { labelKey: 'badge_inDevelopment', color: 'dev' },
    ai_text_process: { labelKey: 'badge_inDevelopment', color: 'dev' },
    send_direct_message: { labelKey: 'badge_new', color: 'new' },
    post_auto_responder: { labelKey: 'badge_new', color: 'new' },
    group_random_messenger: { labelKey: 'badge_new', color: 'new' },
    post_to_fanpage: { labelKey: 'badge_maintenance', color: 'dev' },
    fanpage_comment_on_post: { labelKey: 'badge_maintenance', color: 'dev' },
    fanpage_reply_to_comments: { labelKey: 'badge_maintenance', color: 'dev' },
    fanpage_like_comments: { labelKey: 'badge_maintenance', color: 'dev' },
    fanpage_moderate_comments: { labelKey: 'badge_maintenance', color: 'dev' },
    fanpage_private_reply: { labelKey: 'badge_maintenance', color: 'dev' },
    facebook_read_insight: { labelKey: 'badge_maintenance', color: 'dev' },

  };

  const {
    nodes,
    edges,
    selectedNodeId,
    workflowName,
    workflowDescription,
    isDirty,
    markClean,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    updateNodeData,
    deleteNode,
    setSelectedNodeId,
    setNodes,
    setEdges,
    setWorkflowName,
    setWorkflowDescription,
    getWorkflow,
    loadWorkflow,
    clearWorkflow,
    undo,
    redo,
    canUndo,
    canRedo,
    pushSnapshot,
    invalidNodeIds,
    setInvalidNodeIds,
    copyNode,
    pasteNode,
    duplicateNode,
  } = useWorkflowStore();

  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState<string | null>(null);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [configDisplayMode, setConfigDisplayMode] = useState<'modal' | 'sidebar'>('modal');
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [currentWorkflowId, setCurrentWorkflowId] = useState<string | null>(
    null,
  );
  const [workflowStatus, setWorkflowStatus] = useState<
    "draft" | "active" | "inactive" | "completed"
  >("draft");

  // Notification modal state
  const [notification, setNotification] = useState<{
    show: boolean;
    type: "success" | "error" | "warning" | "info";
    title: string;
    message: string;
  }>({ show: false, type: "info", title: "", message: "" });

  // Drag-to-delete zone
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const deleteZoneRef = useRef<HTMLDivElement>(null);

  // Confirm dialog for the "New" in-page action (not a route change)
  const [showNewConfirm, setShowNewConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  // Multi-workflow template confirm
  const [pendingMultiTemplate, setPendingMultiTemplate] = useState<WorkflowTemplate | null>(null);
  const [showMultiWorkflowConfirm, setShowMultiWorkflowConfirm] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Template setup guide modal
  const [setupGuideTemplate, setSetupGuideTemplate] = useState<WorkflowTemplate | null>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    nodeId: string | null; // null = pane context menu
    flowPosition: { x: number; y: number };
  } | null>(null);
  // Confirm dialog for SPA navigation (history interceptor)
  const [showNavConfirm, setShowNavConfirm] = useState(false);
  const pendingNavRef = useRef<string | null>(null);
  const allowNavRef = useRef(false);

  const params = useParams();
  const navigate = useNavigate();
  const routeLocation = useLocation();

  // ── Unsaved-changes guard ──────────────────────────────────────────────────
  // Intercept window.history.pushState (used by react-router Link / navigate)
  // whenever there are unsaved changes. Works with <BrowserRouter>.
  useEffect(() => {
    if (!isDirty) return;

    const origPushState = window.history.pushState.bind(window.history);
    window.history.pushState = (state: unknown, title: string, url?: string | URL | null) => {
      if (!allowNavRef.current && url != null) {
        pendingNavRef.current = String(url);
        setShowNavConfirm(true);
        return; // hold navigation until user confirms
      }
      origPushState(state, title, url);
    };

    return () => {
      window.history.pushState = origPushState;
    };
  }, [isDirty]);

  const confirmLeave = () => {
    const path = pendingNavRef.current;
    pendingNavRef.current = null;
    setShowNavConfirm(false);
    if (path) {
      allowNavRef.current = true;
      markClean();
      navigate(path, { replace: false });
      // reset flag after react-router has processed the navigation
      setTimeout(() => { allowNavRef.current = false; }, 0);
    }
  };

  const cancelLeave = () => {
    pendingNavRef.current = null;
    setShowNavConfirm(false);
  };

  // Block native browser navigation (tab close / address-bar redirect / F5)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);
  // ──────────────────────────────────────────────────────────────────────────

  // 🆕 Inject registry node types into store when loaded
  const { setNodeTypesRegistry } = useWorkflowStore();
  useEffect(() => {
    if (registryNodeTypes.length > 0) {
      setNodeTypesRegistry(registryNodeTypes);
    }
  }, [registryNodeTypes, setNodeTypesRegistry]);

  // Listen for SSE workflow_completed event relayed via App.tsx custom event
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.workflow_id && detail.workflow_id === currentWorkflowId) {
        setWorkflowStatus("completed");
        window.dispatchEvent(new CustomEvent("workflow-status-changed"));
      }
    };
    window.addEventListener('workflow-one-shot-completed', handler);
    return () => window.removeEventListener('workflow-one-shot-completed', handler);
  }, [currentWorkflowId]);

  // Fetch accounts on mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      // Use dedicated method which calls /api/v1/my-accounts (avoid calling '/accounts' which may be routed to SPA HTML)
      const response = await apiService.getAccounts();
      // backend returns shape like { data: { accounts: [...] }, success: true }
      const accountsList =
        (response && (response.data?.accounts || response.accounts)) || [];
      setAccounts(accountsList);
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
    }
  };

  // fetchWorkflows kept for potential future use
  const fetchWorkflows = async () => {
    try {
      const response = await WorkflowAPI.getAll();
      setWorkflows(response.workflows || []);
    } catch (error) {
      console.error("Failed to fetch workflows:", error);
    }
  };

  // Auto-redirect: when landing on /workflows (no ID), fetch the list
  // and navigate to the first workflow if available.
  // Skip if user just created a new workflow (module-level flag survives remounts).
  useEffect(() => {
    const id = params?.id as string | undefined;
    if (id) return; // already on a specific workflow
    if (_skipAutoRedirect) return; // user explicitly created new

    let cancelled = false;
    (async () => {
      try {
        const response = await WorkflowAPI.getAll();
        // Re-check after async: user might have clicked "New" while fetch was in-flight
        if (cancelled || _skipAutoRedirect) return;
        const list = response.workflows || [];
        if (list.length > 0) {
          const firstId = list[0]._id;
          if (firstId) {
            navigate(`/workflows/${firstId}`, { replace: true });
          }
        }
        // If list is empty, stay on /workflows → shows new workflow screen
      } catch (err) {
        console.error("Failed to fetch workflows for auto-redirect:", err);
      }
    })();

    return () => { cancelled = true; };
  }, [params?.id]);

  // Load workflow when URL param changes (navigate to /workflows/:id)
  useEffect(() => {
    const id = params?.id as string | undefined;
    if (!id) return;

    // avoid reloading if already loaded
    if (currentWorkflowId === id) return;

    _skipAutoRedirect = false; // user navigated to existing workflow — allow future auto-redirect
    setIsLoadingWorkflow(true);
    (async () => {
      try {
        const wf = await WorkflowAPI.getById(id);
        // wf is expected to be full workflow object compatible with store.loadWorkflow
        loadWorkflow(wf as any);
        setCurrentWorkflowId(id);
        setWorkflowStatus(wf.status || "draft");
      } catch (err) {
        console.error("Failed to load workflow from URL param:", err);
        // keep current state unchanged
      } finally {
        setIsLoadingWorkflow(false);
      }
    })();
  }, [params?.id, routeLocation.pathname]);

  // Handle Delete key press (nodes and edges)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Delete") return;
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (selectedNodeId) {
        event.preventDefault();
        // Prevent deleting the last account_selector node
        const targetNode = nodes.find((n) => n.id === selectedNodeId);
        if (targetNode?.type === 'account_selector') {
          const accountSelectorCount = nodes.filter((n) => n.type === 'account_selector').length;
          if (accountSelectorCount <= 1) {
            setNotification({ show: true, type: 'warning', title: t('cannotDelete'), message: t('cannotDeleteDesc') });
            return;
          }
        }
        deleteNode(selectedNodeId);
        setSelectedNodeId(null);
      } else if (selectedEdgeId) {
        event.preventDefault();
        pushSnapshot();
        onEdgesChange([{ id: selectedEdgeId, type: 'remove' }]);
        setSelectedEdgeId(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedNodeId, selectedEdgeId, deleteNode, setSelectedNodeId, onEdgesChange, pushSnapshot]);

  // Handle Undo/Redo keyboard shortcuts
  useEffect(() => {
    const handleUndoRedo = (event: KeyboardEvent) => {
      // Skip if user is typing in an input field
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
      }
      if ((event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.key === 'z' && event.shiftKey))) {
        event.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleUndoRedo);
    return () => window.removeEventListener('keydown', handleUndoRedo);
  }, [undo, redo]);

  // Handle Copy/Paste keyboard shortcuts (Ctrl+C / Ctrl+V)
  useEffect(() => {
    const handleCopyPaste = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        if (selectedNodeId) {
          event.preventDefault();
          copyNode(selectedNodeId);
        }
      }

      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        event.preventDefault();
        pasteNode();
      }
    };

    window.addEventListener('keydown', handleCopyPaste);
    return () => window.removeEventListener('keydown', handleCopyPaste);
  }, [selectedNodeId, copyNode, pasteNode]);

  // Handle F11 fullscreen toggle
  useEffect(() => {
    const handleFullscreenKey = (event: KeyboardEvent) => {
      if (event.key === 'F11') {
        event.preventDefault();
        if (!document.fullscreenElement && !isFullscreen) {
          try {
            document.documentElement.requestFullscreen();
          } catch {
            setIsFullscreen(true);
          }
        } else if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          setIsFullscreen(false);
        }
      }
    };

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    window.addEventListener('keydown', handleFullscreenKey);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      window.removeEventListener('keydown', handleFullscreenKey);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Handle node drag from palette
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      // Handle template drop → create new workflow from template
      const templateId = event.dataTransfer.getData("application/workflow-template");
      if (templateId) {
        const template = WORKFLOW_TEMPLATES.find((t) => t.id === templateId);
        if (!template) return;

        // Multi-workflow template → show confirmation dialog
        if (template.isMultiWorkflow) {
          setPendingMultiTemplate(template);
          setShowMultiWorkflowConfirm(true);
          return;
        }

        const { nodes: tplNodes, edges: tplEdges } = applyTemplate(template);
        _skipAutoRedirect = true;
        setNodes(tplNodes);
        setEdges(tplEdges);
        setWorkflowName(t(template.suggestedNameKey));
        setCurrentWorkflowId(null);
        setWorkflowStatus("draft");
        navigate('/workflows', { replace: true });
        // Show setup guide modal if template has one
        if (template.setupGuide && template.setupGuide.length > 0) {
          setSetupGuideTemplate(template);
        }
        return;
      }

      const type = event.dataTransfer.getData("application/reactflow");
      if (!type || !reactFlowWrapper.current) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const added = addNode(type, position);
      if (added === false) {
        setNotification({
          show: true,
          type: "warning",
          title: t('onlyOneAllowed'),
          message: t('onlyOneAllowedDesc'),
        });
      } else if (added === 'need_account_selector') {
        setNotification({
          show: true,
          type: "warning",
          title: t('selectAccountRequired'),
          message: t('selectAccountRequiredDesc'),
        });
      }
    },
    [screenToFlowPosition, addNode, setNodes, setEdges, setWorkflowName, navigate, t],
  );

  // Handle new workflow — guard against unsaved changes
  const handleNewWorkflow = () => {
    if (isDirty) {
      setShowNewConfirm(true);
      return;
    }
    _skipAutoRedirect = true;
    clearWorkflow();
    setCurrentWorkflowId(null);
    setWorkflowStatus("draft");
    navigate('/workflows', { replace: true });
  };

  const confirmNewWorkflow = () => {
    _skipAutoRedirect = true;
    clearWorkflow();
    setCurrentWorkflowId(null);
    setWorkflowStatus("draft");
    setShowNewConfirm(false);
    navigate('/workflows', { replace: true });
  };

  // Handle starting tour — always on a fresh workflow
  const [showTourConfirm, setShowTourConfirm] = useState(false);

  const resetAndStartTour = () => {
    _skipAutoRedirect = true;
    clearWorkflow();
    setCurrentWorkflowId(null);
    setWorkflowStatus("draft");
    navigate('/workflows', { replace: true });
    // Small delay so the blank canvas renders before tour starts
    setTimeout(() => startTour(true), 300);
  };

  const handleStartTour = () => {
    if (isDirty || currentWorkflowId) {
      setShowTourConfirm(true);
      return;
    }
    resetAndStartTour();
  };

  const confirmStartTour = () => {
    setShowTourConfirm(false);
    resetAndStartTour();
  };

  // Shared validation helper — returns true when all nodes pass
  const runValidation = (): boolean => {
    const validationResults = validateAllNodes(nodes, { availableAccountCount: accounts.length });
    if (validationResults.length > 0) {
      const ids = new Set<string>(validationResults.map((r) => r.nodeId));
      setInvalidNodeIds(ids);
      setTimeout(() => setInvalidNodeIds(new Set()), 4000);
      const summary = validationResults
        .map((r) => `• ${r.nodeLabel}: ${r.errors.join('; ')}`)
        .join('\n');
      setNotification({
        show: true,
        type: "warning",
        title: t('validationFailed'),
        message: `${t('validationFailedPrefix')}\n\n${summary}`,
      });
      return false;
    }
    return true;
  };

  // Handle save workflow (no validation — allows saving templates without data)
  const handleSave = async () => {
    if (currentWorkflowId && workflowStatus === "active") {
      setNotification({
        show: true,
        type: "warning",
        title: t('workflowIsActive'),
        message: t('workflowIsActiveDesc'),
      });
      return;
    }

    setIsSaving(true);
    setIsActionLoading(t('savingWorkflow'));
    try {
      const workflow = getWorkflow();
      let savedId = currentWorkflowId;

      if (currentWorkflowId) {
        await WorkflowAPI.update(currentWorkflowId, workflow);
        markClean();
        setNotification({
          show: true,
          type: "success",
          title: t('savedSuccessfully'),
          message: t('workflowUpdated', { name: workflow.name }),
        });
      } else {
        const created = await WorkflowAPI.create(workflow);
        savedId = created._id || null;
        setCurrentWorkflowId(savedId);
        markClean();
        // Navigate to the newly created workflow's URL so Draft state is cleared
        if (savedId) {
          _skipAutoRedirect = false;
          navigate(`/workflows/${savedId}`, { replace: true });
        }
        setNotification({
          show: true,
          type: "success",
          title: t('createdSuccessfully'),
          message: t('workflowCreated', { name: workflow.name }),
        });
      }
      // Trigger workflow list refresh in sidebar
      window.dispatchEvent(new CustomEvent("workflow-status-changed"));

      // Re-fetch workflow to sync status with backend (e.g. active → show Deactivate)
      if (savedId) {
        try {
          const latest = await WorkflowAPI.getById(savedId);
          if (latest.status) {
            setWorkflowStatus(latest.status);
          }
        } catch (_) { /* non-critical */ }
      }
    } catch (error: any) {
      console.error("Failed to save workflow:", error);
      setNotification({
        show: true,
        type: "error",
        title: t('saveWorkflowFailed'),
        message:
          error.response?.data?.error ||
          error.message ||
          t('unableToSave'),
      });
    } finally {
      setIsSaving(false);
      setIsActionLoading(null);
    }
  };

  // Load workflow removed — use Save/Import/Export instead

  // Handle execute workflow
  const handleExecute = async () => {
    const workflow = getWorkflow();
    const validation = WorkflowConverter.validateWorkflow(workflow);

    if (!validation.valid) {
      alert(t('validationFailed2') + "\n" + validation.errors.join("\n"));
      return;
    }

    setIsActionLoading(t('executingWorkflow'));
    try {
      // Auto-save workflow if not saved yet or update if modified
      let workflowId = currentWorkflowId;

      if (!workflowId) {
        // Create new workflow before executing
        const created = await WorkflowAPI.create(workflow);
        workflowId = created._id || null;
        setCurrentWorkflowId(workflowId);
      } else {
        // Update existing workflow to ensure latest changes are saved
        await WorkflowAPI.update(workflowId, workflow);
      }

      if (!workflowId) {
        alert(t('couldNotSave'));
        return;
      }

      // Call backend execute API (pass selected accounts as override)
      const result = await WorkflowAPI.execute(workflowId, {
        account_ids: selectedAccounts,
      });

      alert(
        `${t('executedSuccessfully')}\n\n` +
          `${result.message}\n` +
          `${t('tasksCreated', { count: result.task_ids.length })}\n` +
          `Task IDs: ${result.task_ids.slice(0, 3).join(", ")}${result.task_ids.length > 3 ? "..." : ""}`,
      );

      setShowExecuteModal(false);
      setSelectedAccounts([]);
    } catch (error: any) {
      console.error("Failed to execute workflow:", error);
      alert(
        t('executeFailed') +
          (error.response?.data?.error || error.message),
      );
    } finally {
      setIsActionLoading(null);
    }
  };

  // Handle export/import
  const handleExport = () => {
    const workflow = getWorkflow();
    // Strip account selections so the exported file is portable.
    // Importers must reconfigure accounts after loading.
    const exported = {
      ...workflow,
      nodes: (workflow.nodes as any[]).map((node) => {
        const data = { ...(node.data as any) };
        for (const key of Object.keys(data)) {
          if (data[key] && typeof data[key] === 'object' && Array.isArray((data[key] as any).selected_accounts)) {
            data[key] = { ...data[key], selected_accounts: [] };
          }
        }
        return { ...node, data };
      }),
    };
    const dataStr = JSON.stringify(exported, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${workflowName.replace(/\s+/g, "_")}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleActivate = async () => {
    if (!currentWorkflowId) {
      setNotification({
        show: true,
        type: "warning",
        title: t('noWorkflowTitle'),
        message: t('noWorkflowMessage'),
      });
      return;
    }

    // Validate all nodes before activating
    if (!runValidation()) return;

    // Auto-save before activating so unsaved changes are persisted in the DB
    // before the worker receives the ExecutionPlan.
    if (isDirty) {
      setIsActionLoading(t('savingWorkflow'));
      try {
        const workflow = getWorkflow();
        await WorkflowAPI.update(currentWorkflowId, workflow);
        markClean();
      } catch (saveError: any) {
        setNotification({
          show: true,
          type: "error",
          title: t('saveWorkflowFailed'),
          message: saveError.response?.data?.error || saveError.message || t('unableToSave'),
        });
        setIsActionLoading(null);
        return;
      }
    }

    setIsActionLoading(t('activatingWorkflow'));
    try {
      await WorkflowAPI.activate(currentWorkflowId);
      setWorkflowStatus("active");
      setNotification({
        show: true,
        type: "success",
        title: t('workflowActivated'),
        message: t('workflowActivatedDesc'),
      });
      // Trigger workflow list refresh in sidebar
      window.dispatchEvent(new CustomEvent("workflow-status-changed"));
    } catch (error: any) {
      setNotification({
        show: true,
        type: "error",
        title: t('activationFailed'),
        message: error.message || t('activationFailed'),
      });
    } finally {
      setIsActionLoading(null);
    }
  };

  const handleDeactivate = async () => {
    if (!currentWorkflowId) return;

    setIsActionLoading(t('deactivatingWorkflow'));
    try {
      await WorkflowAPI.deactivate(currentWorkflowId);
      setWorkflowStatus("inactive");
      setNotification({
        show: true,
        type: "success",
        title: t('workflowDeactivated'),
        message: t('workflowDeactivatedDesc'),
      });
      // Trigger workflow list refresh in sidebar
      window.dispatchEvent(new CustomEvent("workflow-status-changed"));
    } catch (error: any) {
      setNotification({
        show: true,
        type: "error",
        title: t('deactivationFailed'),
        message: error.message || t('deactivationFailed'),
      });
    } finally {
      setIsActionLoading(null);
    }
  };

  const [isReactivating, setIsReactivating] = useState(false);

  const handleRunNow = async () => {
    if (!currentWorkflowId) return;

    // Validate all nodes before running
    if (!runValidation()) return;

    // Auto-save before running
    if (isDirty) {
      setIsActionLoading(t('savingWorkflow'));
      try {
        const workflow = getWorkflow();
        await WorkflowAPI.update(currentWorkflowId, workflow);
        markClean();
      } catch (saveError: any) {
        setNotification({
          show: true,
          type: "error",
          title: t('saveWorkflowFailed'),
          message: saveError.response?.data?.error || saveError.message || t('unableToSave'),
        });
        setIsActionLoading(null);
        return;
      }
    }

    setIsActionLoading('run_now');
    try {
      await WorkflowAPI.runNow(currentWorkflowId);
      setNotification({
        show: true,
        type: "success",
        title: t('runNowSent'),
        message: t('runNowSentDesc'),
      });
    } catch (error: any) {
      setNotification({
        show: true,
        type: "error",
        title: t('runNowFailed'),
        message: error.response?.data?.error || error.message || t('runNowFailedDesc'),
      });
    } finally {
      setIsActionLoading(null);
    }
  };

  const handleReactivate = async () => {
    if (!currentWorkflowId) return;

    // Validate all nodes before reactivating
    if (!runValidation()) return;

    // Auto-save before reactivating
    if (isDirty) {
      setIsActionLoading(t('savingWorkflow'));
      try {
        const workflow = getWorkflow();
        await WorkflowAPI.update(currentWorkflowId, workflow);
        markClean();
      } catch (saveError: any) {
        setNotification({
          show: true,
          type: "error",
          title: t('saveWorkflowFailed'),
          message: saveError.response?.data?.error || saveError.message || t('unableToSave'),
        });
        setIsActionLoading(null);
        return;
      }
    }

    setIsReactivating(true);
    setIsActionLoading(t('reactivatingWorkflow'));
    try {
      await WorkflowAPI.deactivate(currentWorkflowId);
      await WorkflowAPI.activate(currentWorkflowId);
      setWorkflowStatus("active");
      setNotification({
        show: true,
        type: "success",
        title: t('workflowReactivated'),
        message: t('workflowReactivatedDesc'),
      });
      window.dispatchEvent(new CustomEvent("workflow-status-changed"));
    } catch (error: any) {
      setNotification({
        show: true,
        type: "error",
        title: t('reactivationFailed'),
        message: error.message || t('reactivationFailed'),
      });
    } finally {
      setIsReactivating(false);
      setIsActionLoading(null);
    }
  };

  const handleDuplicateWorkflow = async () => {
    if (!currentWorkflowId) return;
    setIsActionLoading('duplicate');
    try {
      const newName = `${workflowName} ${t('copySuffix')}`;
      const result: any = await WorkflowAPI.duplicate(currentWorkflowId, newName);
      setNotification({
        show: true,
        type: 'success',
        title: t('workflowDuplicated'),
        message: t('workflowDuplicatedDesc', { name: newName }),
      });
      window.dispatchEvent(new Event('workflow-status-changed'));
      const newId = result?._id || result?.id;
      if (newId) setTimeout(() => navigate(`/workflows/${newId}`), 1200);
    } catch {
      setNotification({ show: true, type: 'error', title: t('duplicateFailed'), message: '' });
    } finally {
      setIsActionLoading(null);
    }
  };

  const handleRemove = async () => {
    if (!currentWorkflowId) return;

    if (workflowStatus === "active") {
      setNotification({
        show: true,
        type: "warning",
        title: t('cannotRemoveActive'),
        message: t('cannotRemoveActiveDesc'),
      });
      return;
    }

    setShowRemoveConfirm(true);
  };

  const confirmRemove = async () => {
    setShowRemoveConfirm(false);
    if (!currentWorkflowId) return;

    setIsActionLoading(t('removingWorkflow'));
    try {
      await WorkflowAPI.delete(currentWorkflowId);
      setIsActionLoading(null);
      setNotification({
        show: true,
        type: "success",
        title: t('workflowRemoved'),
        message: t('workflowRemovedDesc'),
      });
      // Reset workflow state
      setTimeout(() => {
        window.location.href = "/workflows";
      }, 1500);
    } catch (error: any) {
      setNotification({
        show: true,
        type: "error",
        title: t('removeFailed'),
        message: error.message || t('removeFailed'),
      });
    } finally {
      setIsActionLoading(null);
    }
  };

  // ── Multi-Workflow Template Confirm ─────────────────────────────────
  const confirmMultiWorkflow = async () => {
    setShowMultiWorkflowConfirm(false);
    const template = pendingMultiTemplate;
    setPendingMultiTemplate(null);
    if (!template?.workflows) return;

    setIsActionLoading(t('multiWf_creating'));

    try {
      const results = applyMultiWorkflowTemplate(template, t);
      if (results.length < 2) return;

      // results[0] = Posting ("Đăng Bài"), results[1] = Interaction ("Tương Tác")
      const postingWf = results[0];
      const interactionWf = results[1];

      // Save Interaction workflow as draft via API (secondary)
      const secondPayload = {
        name: interactionWf.name,
        description: '',
        status: 'draft' as const,
        nodes: interactionWf.nodes.filter((n) => n.id !== DATA_ZONE_ID).map((node) => ({
          id: node.id,
          type: node.type,
          position: node.position,
          data: { label: ((node.data as any).label || 'Untitled'), ...node.data },
        })),
        edges: interactionWf.edges.map((e) => ({ id: e.id, source: e.source, target: e.target })),
        config: {
          run_type: 'recurring' as const,
          global_delay: { min_seconds: 30, max_seconds: 60 },
          execution_mode: 'sequential' as const,
          tasks_per_week: 3,
          weekdays: [1, 2, 3, 4, 5, 6, 0],
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Ho_Chi_Minh',
        },
      };
      await WorkflowAPI.create(secondPayload as any);

      // Load Posting workflow on canvas as current unsaved draft (primary focus)
      _skipAutoRedirect = true;
      setNodes(postingWf.nodes);
      setEdges(postingWf.edges);
      setWorkflowName(postingWf.name);
      setCurrentWorkflowId(null);
      setWorkflowStatus("draft");
      navigate('/workflows', { replace: true });

      // Refresh workflow list sidebar
      window.dispatchEvent(new CustomEvent("workflow-status-changed"));

      setNotification({
        show: true,
        type: "success",
        title: t('multiWf_created'),
        message: t('multiWf_createdDesc', { first: postingWf.name, second: interactionWf.name }),
      });
    } catch (error: any) {
      setNotification({
        show: true,
        type: "error",
        title: t('multiWf_failed'),
        message: error.response?.data?.error || error.message || t('unableToSave'),
      });
    } finally {
      setIsActionLoading(null);
    }
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;

    const stripNodes = (nodes: any[]) =>
      (nodes || []).map((node: any) => {
        const data = { ...node.data };
        for (const key of Object.keys(data)) {
          if (data[key] && typeof data[key] === 'object' && Array.isArray((data[key] as any).selected_accounts)) {
            data[key] = { ...data[key], selected_accounts: [] };
          }
        }
        return { ...node, data };
      });

    const readFile = (file: File): Promise<any[]> =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const raw = JSON.parse(e.target?.result as string);
            if (raw?._type === 'workflow_bundle' && Array.isArray(raw.workflows)) {
              resolve(raw.workflows);
            } else {
              resolve([raw]);
            }
          } catch {
            resolve([]);
          }
        };
        reader.readAsText(file);
      });

    Promise.all(files.map(readFile)).then(async (results) => {
      const allWorkflows = results.flat();
      if (allWorkflows.length === 0) {
        setNotification({
          show: true,
          type: 'warning',
          title: t('importFailed'),
          message: t('noValidWorkflowsFound'),
        });
        return;
      }

      if (allWorkflows.length === 1) {
        // Single workflow → load into editor
        const raw = allWorkflows[0];
        const workflow = { ...raw, nodes: stripNodes(raw.nodes || []) };
        _skipAutoRedirect = true;
        setCurrentWorkflowId(null);
        setWorkflowStatus('draft');
        navigate('/workflows', { replace: true });
        loadWorkflow(workflow);
        useWorkflowStore.setState({ isDirty: true });
        setNotification({
          show: true,
          type: 'success',
          title: t('workflowImported'),
          message: t('importedPleaseReview'),
        });
      } else {
        // Multiple workflows → create all via API
        setIsActionLoading(t('importingWorkflows'));
        let successCount = 0;
        let firstId: string | null = null;
        for (const raw of allWorkflows) {
          try {
            const nodes = stripNodes(raw.nodes || []);
            const result: any = await WorkflowAPI.create({
              name: raw.name || 'Imported Workflow',
              description: raw.description || '',
              nodes,
              edges: raw.edges || [],
              config: raw.config || {},
            } as any);
            successCount++;
            if (!firstId) firstId = result?._id || result?.id;
          } catch (e) {
            console.error('Failed to create workflow:', e);
          }
        }
        window.dispatchEvent(new Event('workflow-status-changed'));
        if (firstId) navigate(`/workflows/${firstId}`);
        setNotification({
          show: true,
          type: successCount > 0 ? 'success' : 'error',
          title: t('workflowImported'),
          message: t('importedMultipleDesc', { count: successCount }),
        });
        setIsActionLoading(null);
      }
    });
  };

  const [hoveredNodeType, setHoveredNodeType] = useState<string | null>(null);
  const [libraryMode, setLibraryMode] = useState<"nodes" | "templates">("nodes");

  const handleNodeHover = (nodeType: string) => {
    setHoveredNodeType(nodeType);
  };

  const handleNodeLeave = () => {
    setHoveredNodeType(null);
  };

  // Validate node on unfocus (deselect)
  // Returns true if the node is valid (or skipped), false if invalid (block switch)
  const validateOnUnfocus = useCallback((prevNodeId: string | null): boolean => {
    if (!prevNodeId) return true;
    const node = nodes.find((n) => n.id === prevNodeId);
    if (!node || node.id === DATA_ZONE_ID) return true;
    const errors = validateSingleNode(node, { availableAccountCount: accounts.length });
    if (errors.length > 0) {
      const merged = new Set<string>(Array.from(invalidNodeIds).concat(prevNodeId));
      setInvalidNodeIds(merged);
      setNotification({
        show: true,
        type: "warning",
        title: t('configIncomplete', { label: node.data?.label || node.type }),
        message: errors.join('\n'),
      });
      return false;
    } else {
      // Clear if previously invalid
      if (invalidNodeIds.has(prevNodeId)) {
        const next = new Set(invalidNodeIds);
        next.delete(prevNodeId);
        setInvalidNodeIds(next);
      }
      return true;
    }
  }, [nodes, invalidNodeIds, setInvalidNodeIds, setNotification]);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);
  const nodeData = selectedNode?.data as any;

  return (
    <div ref={builderRootRef} className={`h-full flex flex-col bg-gray-50 ${isFullscreen ? 'fixed inset-0 z-[9990]' : ''}`}>
      {/* Header - Using telegram-ui component */}
      <Header
        title={
          <input
            data-tour="workflow-name-input"
            type="text"
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
            className="text-xl font-bold text-gray-900 border-none outline-none bg-transparent p-0 w-full"
            placeholder={t('workflowName')}
          />
        }
        action={
          <>
            <Button
              data-tour="btn-new"
              variant="primary"
              size="sm"
              onClick={handleNewWorkflow}
              icon={<FilePlus className="w-4 h-4" />}
            >
              {t('new')}
            </Button>
            <div className="block">
              <input
                type="file"
                accept=".json"
                multiple
                onChange={handleImport}
                className="hidden"
                id="workflow-import"
              />
              <label htmlFor="workflow-import">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    document.getElementById("workflow-import")?.click()
                  }
                  icon={<Upload className="w-4 h-4" />}
                >
                  {t('import')}
                </Button>
              </label>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowExportModal(true)}
              icon={<Download className="w-4 h-4" />}
              className="flex"
            >
              {t('export')}
            </Button>
            {currentWorkflowId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDuplicateWorkflow}
                disabled={isActionLoading === 'duplicate'}
                icon={<CopyPlus className="w-4 h-4" />}
                className="flex"
                title={t('duplicate')}
              >
                {isActionLoading === 'duplicate' ? t('duplicatingWorkflow') : t('duplicate')}
              </Button>
            )}
            <Button
              data-tour="btn-save"
              variant="secondary"
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              icon={<Save className="w-4 h-4" />}
            >
              {isSaving ? t('saving') : t('save')}
            </Button>

            {currentWorkflowId && workflowStatus !== "active" && workflowStatus !== "completed" && (
              <Button
                data-tour="btn-activate"
                variant="success"
                size="sm"
                onClick={handleActivate}
                icon={<Power className="w-4 h-4" />}
                className="flex"
              >
                {t('activate')}
              </Button>
            )}
            {currentWorkflowId && workflowStatus === "active" && (
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeactivate}
                icon={<PowerOff className="w-4 h-4" />}
                className="flex"
              >
                {t('deactivate')}
              </Button>
            )}
            {currentWorkflowId && workflowStatus === "active" && (
              <Button
                variant="success"
                size="sm"
                onClick={handleReactivate}
                disabled={isReactivating}
                icon={<RotateCw className={`w-4 h-4 ${isReactivating ? 'animate-spin' : ''}`} />}
                className="flex"
              >
                {isReactivating ? t('reactivating') : t('reactivate')}
              </Button>
            )}
            {/* ⚡ Run Now: trigger an immediate one-shot execution regardless of run_type */}
            {currentWorkflowId && (workflowStatus === "active" || workflowStatus === "inactive" || workflowStatus === "draft") && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRunNow}
                disabled={isActionLoading === 'run_now'}
                icon={<Play className="w-4 h-4" />}
                className="flex"
                title={t('runNowTooltip')}
              >
                {isActionLoading === 'run_now' ? t('runNowSending') : t('runNow')}
              </Button>
            )}
            {currentWorkflowId && (
              <Button
                variant="danger"
                size="sm"
                onClick={handleRemove}
                icon={<Trash2 className="w-4 h-4" />}
                className="flex"
              >
                {t('remove')}
              </Button>
            )}
          </>
        }
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Workflow List Panel - visible in fullscreen */}
        {isFullscreen && (
          <WorkflowListPanel
            isOpen={showWorkflowList}
            onToggle={() => setShowWorkflowList((v) => !v)}
            draftName={!currentWorkflowId ? workflowName : undefined}
          />
        )}

        {/* Left Sidebar - Node Palette */}
        <div data-tour="node-library" className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            {/* Library Mode Tabs */}
            <div className="flex gap-2 mb-4 border-b border-gray-200">
              <button
                onClick={() => setLibraryMode("nodes")}
                className={`flex-1 pb-2 px-2 text-sm font-semibold transition-colors ${
                  libraryMode === "nodes"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t('nodeLibrary')}
              </button>
              <button
                onClick={() => setLibraryMode("templates")}
                className={`flex-1 pb-2 px-2 text-sm font-semibold transition-colors ${
                  libraryMode === "templates"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t('templates')}
              </button>
            </div>

            {/* Node Library Mode */}
            {libraryMode === "nodes" ? (
              <>
                {/* 🆕 Registry Loading State */}
                {registryLoading && (
                  <div className="p-4 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm">{t('loadingNodeTypes')}</p>
                  </div>
                )}

                {/* 🆕 Registry Error State */}
                {registryError && !registryLoading && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-2">
                    <p className="text-red-600 text-sm font-semibold mb-1">
                      {t('failedToLoadRegistry')}
                    </p>
                    <p className="text-red-500 text-xs">{registryError}</p>
                    <p className="text-gray-600 text-xs mt-2">
                      {t('usingFallback')}
                    </p>
                  </div>
                )}

                {/* Node Categories */}
                {!registryLoading && [
                  "control",
                  "data",
                  "action",
                  "timing",
                  "with page",
                  "with post",
                  "with group",
                  "with profile",
                  "messaging",
                  "with tiktok",
                  "with zalo",
                  "with instagram",
                  "with threads",
                  "ai_flow_action",
                  "video_production",
                ]
                  .filter((category) =>
                    NODE_TYPES_TO_USE.some((nt) => nt.category === category),
                  )
                  .map((category) => {
                    const catStyle = CATEGORY_STYLES[category] || { bg: 'bg-white', border: 'border-gray-200', iconBg: 'bg-gray-100', iconText: 'text-gray-600', accent: 'bg-gray-400' };
                    return (
                    <div
                      key={category}
                      data-tour={`category-${category.replace(/\s+/g, '-')}`}

                    >
                    <CollapsibleSection
                      className="mb-2"
                      titleColor="default"
                      title={category}
                      defaultOpen={true}
                    >
                      {NODE_TYPES_TO_USE.filter((nt) => nt.category === category).map(
                        (nodeType) => {
                          const icon = NODE_ICONS[nodeType.type];
                          const isHovered = hoveredNodeType === nodeType.type;
                          const desc = t(`nodeDesc_${nodeType.type}`, { defaultValue: nodeType.shortDescription });
                          const nodeBadge = NODE_BADGES[nodeType.type];
                          return (
                          <div
                            key={nodeType.type}
                            className="relative"
                            onMouseEnter={() =>
                              handleNodeHover(nodeType.type)
                            }
                            onMouseLeave={handleNodeLeave}
                          >
                            {nodeBadge && (
                              <span
                                className={`inline-block relative left-3 -mb-1.5 px-2 py-0.5 text-[10px] font-bold leading-tight rounded-md text-white z-10 ${
                                  nodeBadge.color === 'new'
                                    ? 'bg-green-500'
                                    : 'bg-orange-500'
                                }`}
                                style={{
                                  borderRadius: '6px 6px 6px 0',
                                }}
                              >
                                {t(nodeBadge.labelKey)}
                                {/* Speech bubble tail */}
                                <span
                                  className={`absolute -bottom-1 left-0 w-0 h-0 border-t-[4px] border-r-[5px] border-r-transparent ${
                                    nodeBadge.color === 'new'
                                      ? 'border-t-green-500'
                                      : 'border-t-orange-500'
                                  }`}
                                />
                              </span>
                            )}
                            <div
                              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-move transition-all hover:shadow-md hover:scale-[1.02] active:scale-[0.98] ${catStyle.bg} ${catStyle.border}`}
                              draggable
                              onDragStart={(event: React.DragEvent) => {
                                event.dataTransfer.setData(
                                  "application/reactflow",
                                  nodeType.type,
                                );
                                event.dataTransfer.effectAllowed = "move";
                              }}
                            >
                              <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${catStyle.iconBg} ${catStyle.iconText}`}>
                                {icon || <span className="text-xs">•</span>}
                              </div>
                              <span className="text-sm font-medium text-gray-800 leading-tight">
                                {nodeType.label}
                              </span>
                            </div>
                            {/* Inline description on hover */}
                            {isHovered && desc && (
                              <div className="mt-1 mx-1 mb-1 px-3 py-2 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 text-xs text-blue-800 leading-relaxed animate-fade-in shadow-sm">
                                <div className="flex items-start gap-1.5">
                                  <span className="text-blue-400 mt-0.5 shrink-0">💡</span>
                                  <span>{desc}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          );
                        },
                      )}
                    </CollapsibleSection>
                    </div>
                    );
                  })}
              </>
            ) : (
              // Template Library Mode — draggable template cards
              <div className="space-y-3 px-0.5">
                {WORKFLOW_TEMPLATES.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="group relative p-3 rounded-xl border transition-all border-gray-200 bg-gradient-to-br from-white to-gray-50 cursor-grab active:cursor-grabbing hover:shadow-md hover:border-blue-300"
                    draggable
                    onDragStart={(event: React.DragEvent) => {
                      event.dataTransfer.setData("application/workflow-template", tpl.id);
                      event.dataTransfer.effectAllowed = "move";

                      // Create a drag image clone outside the overflow container so it doesn't get clipped
                      const el = event.currentTarget as HTMLElement;
                      const clone = el.cloneNode(true) as HTMLElement;
                      clone.style.position = 'fixed';
                      clone.style.top = '-9999px';
                      clone.style.left = '-9999px';
                      clone.style.width = `${el.offsetWidth}px`;
                      clone.style.zIndex = '99999';
                      clone.style.opacity = '0.9';
                      clone.style.pointerEvents = 'none';
                      clone.style.borderRadius = '12px';
                      clone.style.boxShadow = '0 8px 24px rgba(59,130,246,0.25)';
                      clone.style.border = '2px solid #3b82f6';
                      clone.style.background = 'white';
                      document.body.appendChild(clone);
                      event.dataTransfer.setDragImage(clone, el.offsetWidth / 2, 20);
                      // Clean up clone after browser captures it
                      requestAnimationFrame(() => {
                        document.body.removeChild(clone);
                      });
                    }}
                  >
                    {/* ? help button — shows setup guide modal */}
                    {tpl.setupGuide && tpl.setupGuide.length > 0 && (
                      <button
                        className="absolute top-2 right-2 z-10 flex items-center justify-center rounded-full text-[11px] font-bold transition-all"
                        style={{ width: 20, height: 20, background: '#EEF2FF', color: '#6C63FF', border: '1.5px solid #C7D2FE', lineHeight: 1 }}
                        title="Xem hướng dẫn cấu hình"
                        onClick={(e) => { e.stopPropagation(); setSetupGuideTemplate(tpl); }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        ?
                      </button>
                    )}
                    <div className="flex items-start gap-2.5">
                      <span className="text-2xl shrink-0 mt-0.5">{tpl.icon}</span>
                      <div className="flex-1 min-w-0" style={{ paddingRight: tpl.setupGuide?.length ? 20 : 0 }}>
                        <p className="text-sm font-semibold text-gray-800 leading-tight">
                          {t(tpl.nameKey)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          {t(tpl.descKey)}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {tpl.nodes.filter((n) => n.type !== 'account_selector').map((n) => (
                            <span
                              key={n.tplId}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100"
                            >
                              {n.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Hover overlay — animated arrow shooting toward canvas */}
                    {<div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-600/85 to-blue-500/75 backdrop-blur-[2px] flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none overflow-hidden">
                      {/* Shooting arrow trails */}
                      <div className="absolute inset-0">
                        <div className="tpl-arrow-trail tpl-arrow-trail--1" />
                        <div className="tpl-arrow-trail tpl-arrow-trail--2" />
                        <div className="tpl-arrow-trail tpl-arrow-trail--3" />
                      </div>
                      {/* Arrow icon + text */}
                      <div className="relative flex items-center gap-2">
                        <span className="text-sm font-bold text-white tracking-wide">{t('dragToCanvas')}</span>
                        <svg className="w-6 h-6 text-white tpl-arrow-shoot" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                    </div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Center - Canvas */}
        <div data-tour="canvas-area" className="flex-1 relative" ref={reactFlowWrapper} onContextMenu={(e) => e.preventDefault()}>
          {isLoadingWorkflow && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
              <LoadingSpinner size="md" text={t('loadingWorkflow')} />
            </div>
          )}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={customNodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeDragStart={(_, node) => {
              setContextMenu(null);
              if (node.id !== DATA_ZONE_ID) {
                pushSnapshot();
                setDraggingNodeId(node.id);
              }
            }}
            onNodeDrag={(event) => {
              // Update delete zone hover state based on mouse position
              if (deleteZoneRef.current) {
                const rect = deleteZoneRef.current.getBoundingClientRect();
                const mouseX = (event as unknown as MouseEvent).clientX;
                const mouseY = (event as unknown as MouseEvent).clientY;
                const isOver = mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom;
                deleteZoneRef.current.setAttribute('data-hovering', isOver ? 'true' : 'false');
              }
            }}
            onNodeDragStop={(event, node) => {
              setDraggingNodeId(null);

              // Check if dropped on delete zone
              if (deleteZoneRef.current) {
                const rect = deleteZoneRef.current.getBoundingClientRect();
                const mouseX = (event as unknown as MouseEvent).clientX;
                const mouseY = (event as unknown as MouseEvent).clientY;
                const isOver = mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom;
                if (isOver && node.id !== DATA_ZONE_ID) {
                  // Prevent deleting last account_selector
                  if (node.type === 'account_selector') {
                    const count = nodes.filter((n) => n.type === 'account_selector').length;
                    if (count <= 1) {
                      setNotification({ show: true, type: 'warning', title: t('cannotDelete'), message: t('cannotDeleteDesc') });
                      return;
                    }
                  }
                  deleteNode(node.id);
                  setSelectedNodeId(null);
                  return;
                }
              }

              // Snap data nodes back to their zone slot
              if (DATA_NODE_TYPES.includes(node.type || '')) {
                const zoneNode = nodes.find((n) => n.id === DATA_ZONE_ID);
                const slot = ZONE_SLOTS.find((s) => s.type === node.type);
                if (zoneNode && slot) {
                  const snapPos = {
                    x: zoneNode.position.x + slot.x,
                    y: zoneNode.position.y + slot.y,
                  };
                  onNodesChange([{
                    id: node.id,
                    type: 'position',
                    position: snapPos,
                  }]);
                }
              }
            }}
            onNodeClick={(_, node) => {
              // Validate previously selected node before switching
              if (selectedNodeId && selectedNodeId !== node.id) {
                const isValid = validateOnUnfocus(selectedNodeId);
                if (!isValid) return; // Block focus switch if validation fails
              }
              setSelectedNodeId(node.id); setSelectedEdgeId(null);
            }}
            onEdgeClick={(_, edge) => {
              if (selectedNodeId) {
                const isValid = validateOnUnfocus(selectedNodeId);
                if (!isValid) return; // Block focus switch if validation fails
              }
              setSelectedEdgeId(edge.id); setSelectedNodeId(null);
            }}
            onPaneClick={() => {
              if (selectedNodeId) {
                const isValid = validateOnUnfocus(selectedNodeId);
                if (!isValid) return; // Block focus switch if validation fails
              }
              setSelectedNodeId(null); setSelectedEdgeId(null);
              setContextMenu(null);
            }}
            onNodeContextMenu={(event, node) => {
              event.preventDefault();
              if (node.id === DATA_ZONE_ID) return;
              setContextMenu({
                x: event.clientX,
                y: event.clientY,
                nodeId: node.id,
                flowPosition: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
              });
            }}
            onPaneContextMenu={(event) => {
              event.preventDefault();
              setContextMenu({
                x: event.clientX,
                y: event.clientY,
                nodeId: null,
                flowPosition: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
              });
            }}
            onMoveStart={() => setContextMenu(null)}
            fitView
            className="bg-gray-50"
            defaultEdgeOptions={{
              type: 'default',
              style: { stroke: '#94a3b8', strokeWidth: 2 },
              animated: false,
            }}
            proOptions={{ hideAttribution: true }}
          >
            <Background />
            <Controls />
            <Panel position="top-left" className="bg-white p-2 rounded shadow flex items-center gap-3">
              <div className="text-xs text-gray-600">
                {t('nodesCount', { count: nodes.filter(n => n.id !== 'data-zone').length })} | {t('edgesCount', { count: edges.length })}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={undo}
                  disabled={!canUndo}
                  title={t('undoTooltip')}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Undo2 className="w-4 h-4 text-gray-600" />
                </button>
                <button
                  onClick={redo}
                  disabled={!canRedo}
                  title={t('redoTooltip')}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Redo2 className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <div className="w-px h-4 bg-gray-300" />
              <button
                onClick={() => {
                  if (!document.fullscreenElement && !isFullscreen) {
                    try {
                      document.documentElement.requestFullscreen();
                    } catch {
                      setIsFullscreen(true);
                    }
                  } else if (document.fullscreenElement) {
                    document.exitFullscreen();
                  } else {
                    setIsFullscreen(false);
                  }
                }}
                title={isFullscreen ? t('exitFullscreen') : t('fullscreen')}
                className="p-1 rounded hover:bg-gray-100 transition-colors"
              >
                {isFullscreen
                  ? <Minimize className="w-4 h-4 text-gray-600" />
                  : <Maximize className="w-4 h-4 text-gray-600" />
                }
              </button>
            </Panel>
          </ReactFlow>

          {/* Tour guide floating button */}
          {!draggingNodeId && (
            <button
              className="tour-trigger-btn-floating"
              onClick={handleStartTour}
              title={t('tour_startTour')}
            >
              ?
            </button>
          )}

          {/* Delete drop zone - visible when dragging a node */}
          {draggingNodeId && (
            <div
              ref={deleteZoneRef}
              data-hovering="false"
              className="absolute bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-4 rounded-xl border-2 border-dashed transition-all duration-200 pointer-events-auto
                border-red-300 bg-red-50/80 text-red-400
                data-[hovering=true]:border-red-500 data-[hovering=true]:bg-red-100 data-[hovering=true]:text-red-600 data-[hovering=true]:scale-110 data-[hovering=true]:shadow-lg"
            >
              <Trash2 className="w-5 h-5" />
              <span className="text-sm font-medium">{t('dropToDelete')}</span>
            </div>
          )}

          {/* Context menu */}
          {contextMenu && (
            <ContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              onClose={() => setContextMenu(null)}
              items={
                contextMenu.nodeId
                  ? [
                      { type: 'header', label: t('nodeOptions') },
                      {
                        label: t('copy'),
                        icon: <Copy className="w-4 h-4" />,
                        shortcut: "Ctrl+C",
                        onClick: () => copyNode(contextMenu.nodeId!),
                      },
                      {
                        label: t('duplicate'),
                        icon: <CopyPlus className="w-4 h-4" />,
                        onClick: () => duplicateNode(contextMenu.nodeId!),
                      },
                      { type: 'separator' },
                      {
                        label: t('deleteNode'),
                        icon: <Trash2 className="w-4 h-4" />,
                        shortcut: "Del",
                        danger: true,
                        onClick: () => {
                          const targetNode = nodes.find((n) => n.id === contextMenu.nodeId);
                          if (targetNode?.type === 'account_selector') {
                            const count = nodes.filter((n) => n.type === 'account_selector').length;
                            if (count <= 1) {
                              setNotification({ show: true, type: 'warning', title: t('cannotDelete'), message: t('cannotDeleteDesc') });
                              return;
                            }
                          }
                          deleteNode(contextMenu.nodeId!);
                          setSelectedNodeId(null);
                        },
                      },
                    ]
                  : [
                      { type: 'header', label: t('canvas') },
                      {
                        label: t('paste'),
                        icon: <ClipboardPaste className="w-4 h-4" />,
                        shortcut: "Ctrl+V",
                        disabled: !useWorkflowStore.getState()._clipboardNode,
                        onClick: () => pasteNode(contextMenu.flowPosition),
                      },
                      { type: 'separator' },
                      {
                        label: t('view'),
                        icon: <Eye className="w-4 h-4" />,
                        onClick: () => {},
                        children: [
                          {
                            label: t('nodeConfiguration'),
                            icon: <PanelRight className="w-4 h-4" />,
                            onClick: () => {},
                            children: [
                              {
                                label: t('modal'),
                                icon: <SquareStack className="w-6 h-6" />,
                                variant: 'card' as const,
                                active: configDisplayMode === 'modal',
                                onClick: () => setConfigDisplayMode('modal'),
                              },
                              {
                                label: t('sidebar'),
                                icon: <PanelRight className="w-6 h-6" />,
                                variant: 'card' as const,
                                active: configDisplayMode === 'sidebar',
                                onClick: () => setConfigDisplayMode('sidebar'),
                              },
                            ],
                          },
                        ],
                      },
                      { type: 'separator' },
                      { type: 'header', label: t('workflowName') },
                      {
                        label: t('newWorkflow'),
                        icon: <FilePlus className="w-4 h-4" />,
                        onClick: () => handleNewWorkflow(),
                      },
                      {
                        label: t('save'),
                        icon: <Save className="w-4 h-4" />,
                        shortcut: "Ctrl+S",
                        disabled: isSaving,
                        onClick: () => handleSave(),
                      },
                      // Activate — only for saved, non-active workflows
                      ...(currentWorkflowId && workflowStatus !== "active" ? [{
                        label: t('activate'),
                        icon: <Power className="w-4 h-4" />,
                        onClick: () => handleActivate(),
                      }] : []),
                      // Deactivate — only for active workflows
                      ...(currentWorkflowId && workflowStatus === "active" ? [{
                        label: t('deactivate'),
                        icon: <PowerOff className="w-4 h-4" />,
                        onClick: () => handleDeactivate(),
                      }] : []),
                      // Reactivate — only for active workflows
                      ...(currentWorkflowId && workflowStatus === "active" ? [{
                        label: t('reactivate'),
                        icon: <RotateCw className="w-4 h-4" />,
                        onClick: () => handleReactivate(),
                      }] : []),
                      // Remove — only for saved workflows
                      ...(currentWorkflowId ? [
                        { type: 'separator' as const },
                        {
                          label: t('removeWorkflow'),
                          icon: <Trash2 className="w-4 h-4" />,
                          danger: true,
                          onClick: () => handleRemove(),
                        },
                      ] : []),
                    ]
              }
            />
          )}
        </div>

        {/* Right Sidebar - Node Editor (sidebar mode) */}
        {selectedNode && configDisplayMode === 'sidebar' && (
          <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto flex-shrink-0 relative">
            {/* Prominent toggle button */}
            <button
              onClick={() => setConfigDisplayMode('modal')}
              title={t('switchToModal')}
              className="absolute -left-4 top-4 z-10 bg-blue-500 hover:bg-blue-600 text-white shadow-lg rounded-full p-1.5 transition-all hover:scale-110 animate-bounce"
              style={{ animationDuration: '2s', animationIterationCount: '3' }}
            >
              <ChevronsLeft className="w-5 h-5" />
            </button>
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700">{t('nodeConfiguration')}</h2>
              </div>

              <div className="mb-4 flex items-center gap-2">
                <Badge variant="primary">{selectedNode.type}</Badge>
                <h3 className="text-lg font-medium">{nodeData.label}</h3>
              </div>

              <div className="space-y-3">
                <Input
                  label={t('nodeLabel')}
                  value={nodeData.label}
                  onChange={(e) =>
                    updateNodeData(selectedNode.id, {
                      ...nodeData,
                      label: e.target.value,
                    })
                  }
                />
                <NodeConfigPanel
                  node={selectedNode as any}
                  onUpdate={(nodeId, newData) => updateNodeData(nodeId, newData)}
                />
              </div>

              <div className="mt-6">
                <Button
                  variant="danger"
                  size="sm"
                  fullWidth
                  onClick={() => {
                    if (selectedNode.type === 'account_selector') {
                      const accountSelectorCount = nodes.filter((n) => n.type === 'account_selector').length;
                      if (accountSelectorCount <= 1) {
                        setNotification({ show: true, type: 'warning', title: t('cannotDelete'), message: t('cannotDeleteDesc') });
                        return;
                      }
                    }
                    deleteNode(selectedNode.id);
                    setSelectedNodeId(null);
                  }}
                  icon={<Trash2 className="w-4 h-4" />}
                >
                  {t('deleteNode')}
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Node Configuration Modal (modal mode) */}
      {selectedNode && configDisplayMode === 'modal' && (
        <>
          {/* Toggle button - hugs the right edge of the modal */}
          <button
            onClick={() => setConfigDisplayMode('sidebar')}
            title={t('switchToSidebar')}
            className="fixed top-1/2 -translate-y-1/2 z-[10000] bg-blue-500 hover:bg-blue-600 text-white shadow-lg rounded-full p-2 transition-all hover:scale-110"
            style={{ left: 'calc(50% + 340px)' }}
          >
            <ChevronsRight className="w-5 h-5 animate-pulse" />
          </button>

          <Modal
            isOpen={!!selectedNode}
            onClose={() => {
              const isValid = validateOnUnfocus(selectedNodeId);
              if (!isValid) return;
              setSelectedNodeId(null);
            }}
            title={t('nodeConfiguration')}
            size="md"
            closeOnBackdropClick={true}
          >
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge variant="primary">{selectedNode.type}</Badge>
                <h3 className="text-lg font-medium">
                  {nodeData.label}
                </h3>
              </div>

              <Input
                label={t('nodeLabel')}
                value={nodeData.label}
                onChange={(e) =>
                  updateNodeData(selectedNode.id, {
                    ...nodeData,
                    label: e.target.value,
                  })
                }
              />

              <NodeConfigPanel
                node={selectedNode as any}
                onUpdate={(nodeId, newData) =>
                  updateNodeData(nodeId, newData)
                }
              />

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    if (selectedNode.type === 'account_selector') {
                      const accountSelectorCount = nodes.filter((n) => n.type === 'account_selector').length;
                      if (accountSelectorCount <= 1) {
                        setNotification({ show: true, type: 'warning', title: t('cannotDelete'), message: t('cannotDeleteDesc') });
                        return;
                      }
                    }
                    deleteNode(selectedNode.id);
                    setSelectedNodeId(null);
                  }}
                  icon={<Trash2 className="w-4 h-4" />}
                >
                  {t('deleteNode')}
                </Button>
                <div className="flex-1" />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const isValid = validateOnUnfocus(selectedNodeId);
                    if (!isValid) return;
                    setSelectedNodeId(null);
                  }}
                >
                  {t('close')}
                </Button>
              </div>
            </div>
          </Modal>
        </>
      )}

      {/* Execute Modal */}
      {showExecuteModal && (
        <Modal
          isOpen={showExecuteModal}
          onClose={() => setShowExecuteModal(false)}
          title={t('executeWorkflow')}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('selectAccountsToRun')}
            </p>

            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded p-2">
              {accounts.map((account) => (
                <label
                  key={account._id}
                  className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedAccounts.includes(account._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAccounts([...selectedAccounts, account._id]);
                      } else {
                        setSelectedAccounts(
                          selectedAccounts.filter((id) => id !== account._id),
                        );
                      }
                    }}
                  />
                  <span className="text-sm">
                    {account.username || account.email}
                  </span>
                  <Badge variant="primary">{account.platform}</Badge>
                </label>
              ))}
            </div>

            <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
              {t('tasksPerAccountSummary', {
                count: nodes.filter(
                  (n) =>
                    n.data.type !== "start" &&
                    n.data.type !== "end" &&
                    n.data.type !== "delay",
                ).length,
                selected: selectedAccounts.length,
              })}
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowExecuteModal(false)}
              >
                {t('cancel')}
              </Button>
              <Button
                variant="success"
                fullWidth
                onClick={handleExecute}
                icon={<Play className="w-4 h-4" />}
              >
                {t('startExecution')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        title={notification.title}
        message={notification.message}
        type={notification.type}
      />

      {/* Unsaved Changes Guard — SPA navigation (history interceptor) */}
      <ConfirmDialog
        isOpen={showNavConfirm}
        onClose={cancelLeave}
        onConfirm={confirmLeave}
        variant="danger"
        title={t('unsavedChanges')}
        message={t('leaveMessage')}
        confirmLabel={t('leaveWithoutSaving')}
        cancelLabel={t('stayHere')}
      />

      {/* Unsaved Changes Guard — New Workflow in-page action */}
      <ConfirmDialog
        isOpen={showNewConfirm}
        onClose={() => setShowNewConfirm(false)}
        onConfirm={confirmNewWorkflow}
        variant="warning"
        title={t('unsavedChanges')}
        message={t('newWorkflowUnsaved', { name: workflowName })}
        confirmLabel={t('discardAndCreateNew')}
        cancelLabel={t('stayHere')}
      />

      {/* Confirm starting tour on a fresh workflow */}
      <ConfirmDialog
        isOpen={showTourConfirm}
        onClose={() => setShowTourConfirm(false)}
        onConfirm={confirmStartTour}
        variant="info"
        title={t('tour_newWorkflowTitle')}
        message={t('tour_newWorkflowMessage')}
        confirmLabel={t('tour_newWorkflowConfirm')}
        cancelLabel={t('cancel')}
      />

      {/* Remove Workflow Confirmation */}
      <ConfirmDialog
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={confirmRemove}
        variant="danger"
        title={t('removeWorkflowTitle')}
        message={t('removeWorkflowMessage', { name: workflowName })}
        confirmLabel={t('remove')}
        cancelLabel={t('cancel')}
      />

      {/* Multi-Workflow Template Confirmation */}
      <ConfirmDialog
        isOpen={showMultiWorkflowConfirm}
        onClose={() => { setShowMultiWorkflowConfirm(false); setPendingMultiTemplate(null); }}
        onConfirm={confirmMultiWorkflow}
        variant="info"
        title={t('multiWf_confirmTitle')}
        message={t('multiWf_confirmMessage')}
        confirmLabel={t('multiWf_confirmBtn')}
        cancelLabel={t('cancel')}
      />

      {/* Template Setup Guide Modal */}
      {setupGuideTemplate && setupGuideTemplate.setupGuide && (
        <TemplateSetupGuideModal
          template={setupGuideTemplate}
          templateName={t(setupGuideTemplate.nameKey)}
          onClose={() => setSetupGuideTemplate(null)}
        />
      )}

      {/* Load modal removed intentionally */}

      {/* Action Loading Overlay */}
      {isActionLoading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl px-8 py-6 flex flex-col items-center gap-3">
            <LoadingSpinner size="md" />
            <p className="text-sm font-medium text-gray-700">{isActionLoading}</p>
          </div>
        </div>
      )}
      {showExportModal && (
        <WorkflowExportModal
          onClose={() => setShowExportModal(false)}
          currentWorkflowId={currentWorkflowId}
        />
      )}
    </div>
  );
};

const WorkflowBuilder: React.FC = () => {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderInner />
    </ReactFlowProvider>
  );
};

export default WorkflowBuilder;
